import { createElement, useEffect } from "react";
import { toast } from "sonner";
import { useGetKpsRecalculation } from "@/resources/kps-recalculation";
import { ACCOUNT_ROLE } from "@/constants";
import type { AccountResponse, KpsRecalculationRun } from "@/types";
import { KpsRecalculationToast } from "./KpsRecalculationToast";

// One stable id so a fresh `toast.custom(...)` call replaces the same toast in place rather than
// stacking a new one on every 10s poll, and so a terminal transition can `toast.dismiss` it by id.
const TOAST_ID = "kps-recalculation-progress";

// `decisions/toast-dismissal-is-a-browser-fact.md` -- both keys carry `run.id`, which is what makes
// a dismissal (and a terminal toast) expire on its own: a later run has a different key, so nothing
// has to be cleared and there is no reset step anywhere.
function dismissedKey(userId: number, runId: number): string {
  return `kps-recalc:dismissed:${userId}:${runId}`;
}

function terminalShownKey(userId: number, runId: number): string {
  return `kps-recalc:terminal-shown:${userId}:${runId}`;
}

// `localStorage` can throw (private-mode quota, disabled storage). Any of these failing just means
// the dismissal/terminal-guard does not survive a reload for this browser -- not worth surfacing to
// the admin, and never worth crashing the toast over.
function readFlag(key: string): boolean {
  try {
    return localStorage.getItem(key) !== null;
  } catch {
    return false;
  }
}

function setFlag(key: string): void {
  try {
    localStorage.setItem(key, "1");
  } catch {
    // See readFlag.
  }
}

// AUT-282: the success toast's body. `updated_keywords`/`skipped_keywords` are real columns that
// are null for every run recorded before this change and for any run that has not yet reached
// `succeeded` -- both fall back to the pre-existing generic sentence rather than rendering "null"
// or "0 of 0". `skipped_keywords === 0` is a real, meaningful count (nothing was skipped), so it
// drops the clause entirely rather than saying "0 skipped". `total_keywords` can be null while the
// counts are present, in which case the "of <total>" half is dropped rather than printing "of null".
export function formatKpsRecalculationSuccessDescription(
  run: Pick<KpsRecalculationRun, "total_keywords" | "updated_keywords" | "skipped_keywords">
): string {
  const { total_keywords, updated_keywords, skipped_keywords } = run;

  if (updated_keywords === null || skipped_keywords === null) {
    return "Stored KPS scores have been updated for every keyword.";
  }

  const updatedClause =
    total_keywords === null
      ? `${updated_keywords.toLocaleString()} keywords updated`
      : `${updated_keywords.toLocaleString()} of ${total_keywords.toLocaleString()} keywords updated`;

  if (skipped_keywords === 0) {
    return updatedClause;
  }

  return `${updatedClause}, ${skipped_keywords.toLocaleString()} skipped — no relevance score`;
}

/**
 * FR-8 to FR-12 (`components/keyword.md § The toast`). Mounted once in `AuthenticatedApp`
 * (`ProtectedRoute.tsx`), beside the Echo subscriptions, so the in-progress toast is live on every
 * authenticated screen for every `owner`/`admin`. Renders through `toast.custom` on the single
 * existing `<Sonner />` mount (`constraints.md § C-28`) -- no `position` is ever passed here.
 *
 * `useGetKpsRecalculation` is enabled only for `owner`/`admin` (`decisions/kps-recalculation-
 * progress-is-polled.md`), so a `viewer` or `editor` session issues no request at all.
 *
 * Per-user, per-run dismissal and the "terminal toast fires once" guard are both browser facts, not
 * database rows (`decisions/toast-dismissal-is-a-browser-fact.md`): `localStorage`, keyed on
 * `run.id`, read on every poll result (via `dataUpdatedAt`, not `data`'s reference -- TanStack
 * Query's structural sharing can keep `data` referentially identical across two polls that returned
 * the same values, but `dataUpdatedAt` still advances on every resolved poll) rather than only at
 * mount, so a dismissal in one tab is picked up by every other tab within one poll interval.
 *
 * A failed poll leaves `data` (and therefore the toast) exactly as it was: this hook never reads
 * `isError`/`error`, so a transient network failure neither dismisses the toast nor shows an error.
 */
export function useKpsRecalculationToast(account: AccountResponse | undefined): void {
  const isAdmin = account?.role === ACCOUNT_ROLE.OWNER || account?.role === ACCOUNT_ROLE.ADMIN;

  const { data, dataUpdatedAt } = useGetKpsRecalculation(isAdmin);

  useEffect(() => {
    if (!isAdmin || !account) {
      return;
    }

    const run: KpsRecalculationRun | null | undefined = data?.run;
    if (!run) {
      toast.dismiss(TOAST_ID);
      return;
    }

    if (run.status === "queued" || run.status === "running") {
      if (readFlag(dismissedKey(account.id, run.id))) {
        toast.dismiss(TOAST_ID);
        return;
      }

      toast.custom(
        () =>
          createElement(KpsRecalculationToast, {
            run,
            onDismiss: () => {
              setFlag(dismissedKey(account.id, run.id));
              toast.dismiss(TOAST_ID);
            },
          }),
        { id: TOAST_ID, duration: Infinity }
      );
      return;
    }

    // Terminal (succeeded/failed): the in-progress toast disappears for every watching admin
    // (FR-11, FR-12), whether or not this user gets a terminal toast of their own.
    toast.dismiss(TOAST_ID);

    if (readFlag(terminalShownKey(account.id, run.id))) {
      return;
    }

    if (run.status === "succeeded") {
      // FR-11: only the admin who triggered the run gets the success toast. Every other admin's
      // in-progress toast has already been dismissed above, and that is all they see.
      if (run.triggered_by_user_id === account.id) {
        toast.success("KPS recalculation complete", {
          description: formatKpsRecalculationSuccessDescription(run),
        });
      }
      setFlag(terminalShownKey(account.id, run.id));
    } else if (run.status === "failed") {
      // FR-12: every owner/admin gets the error toast, and there is no retry action -- rows left at
      // `kps = -1` are recovered by the existing `RECALC_PENDING_KPS_TASK`, not by anything here.
      toast.error("KPS recalculation failed", {
        description: run.error_message ?? "Stored KPS scores were not recalculated.",
      });
      setFlag(terminalShownKey(account.id, run.id));
    }
  }, [dataUpdatedAt, isAdmin, account, data]);
}
